import { getBusinessClient } from '@beatq/sdk-client';
import {
  mapErrorCode,
  readDataFromEnvelope,
  readListFromEnvelope,
  readObjectFieldFromEnvelope,
  setAccessToken,
} from '@beatq/shared';
import { setStoredBusinessAccountId } from '../../shell/businessContext.js';
import { checklistProgressPercent, resolveChecklistSteps } from './signupSetupState.js';

type ApiErr = { error?: { code: string; message: string } };

export type BusinessAccount = {
  id?: string;
  legalName?: string | null;
  tradingName?: string | null;
  billingEmail?: string | null;
  contactName?: string | null;
  expectedVolume?: string | null;
  status?: string | null;
  state?: string | null;
  verificationState?: string | null;
  setupProgress?: number | null;
  spendToDateMinor?: number | null;
  budgetRemainingMinor?: number | null;
  pendingApprovalsCount?: number | null;
  invoiceState?: string | null;
  creditLimitMinor?: number | null;
  creditUsedMinor?: number | null;
  creditUsed?: number | null;
  paymentTerms?: string | null;
  invoicingFrequency?: string | null;
  billingCycle?: string | null;
  onboardedAt?: string | null;
};

export type SetupChecklistStep = {
  code: string;
  label: string;
  status?: string | null;
  blockers?: string[];
  route?: string;
  screenId?: string;
  optional?: boolean;
};

export type VerificationEvidenceItem = {
  id?: string;
  evidenceType?: string;
  reference?: string | null;
  notes?: string | null;
  filename?: string | null;
  contentType?: string | null;
  sizeBytes?: number | null;
  hasBinary?: boolean;
  state?: string;
  createdAt?: string;
};

export type AccountOverview = {
  account?: BusinessAccount;
  pendingApprovals?: unknown[];
  invoices?: unknown[];
  budgets?: unknown[];
  failedRegions?: string[];
};

export type VerificationSummary = {
  account?: BusinessAccount;
  verificationState: string;
  requiredEvidence: string[];
  items: VerificationEvidenceItem[];
  source: 'verification' | 'account-fallback';
};

export type SetupChecklist = {
  account?: BusinessAccount;
  steps: SetupChecklistStep[];
  progress: number;
  goLiveReady?: boolean;
  source: 'setup-checklist' | 'account-fallback';
};

export function sdkErrorMessage(error: unknown): string {
  const e = error as ApiErr;
  return e?.error?.message ?? mapErrorCode(e?.error?.code ?? 'ERR-UNKNOWN');
}

export function isStepUpRequiredError(error: unknown): boolean {
  if (error == null) return false;
  const apiErr = error as { error?: { code?: string }; code?: string; needsMfa?: boolean };
  if (apiErr.needsMfa) return true;
  const code = (apiErr.error?.code ?? apiErr.code ?? '').toUpperCase();
  if (code === 'ERR-AUTH-0051') return true;
  const message = error instanceof Error ? error.message : String(error);
  return /extra confirmation is required/i.test(message);
}

export function accountFromEnvelope(envelope: unknown): BusinessAccount | undefined {
  return (
    readObjectFieldFromEnvelope<BusinessAccount>(envelope, ['account']) ??
    readDataFromEnvelope<BusinessAccount>(envelope)
  );
}

export function itemsFromEnvelope(envelope: unknown): unknown[] {
  return readListFromEnvelope(envelope, ['items', 'approvals', 'invoices', 'budgets']);
}

export type UnusualSpendSignal = {
  label: string;
  percentAhead: number | null;
};

function unusualSpendSignalFromRow(row: Record<string, unknown>): UnusualSpendSignal | null {
  const nestedPattern =
    (row.spendPattern != null && typeof row.spendPattern === 'object'
      ? (row.spendPattern as Record<string, unknown>)
      : null) ??
    (row.spendPattern != null && typeof row.spendPattern === 'object'
      ? (row.spendPattern as Record<string, unknown>)
      : null);
  if (row.unusualSpend === false || nestedPattern?.unusualSpend === false) return null;
  const name =
    (typeof row.name === 'string' && row.name.trim()) ||
    (typeof row.label === 'string' && row.label.trim()) ||
    (typeof nestedPattern?.name === 'string' && nestedPattern.name.trim()) ||
    '';
  const percentAhead =
    typeof nestedPattern?.percentAhead === 'number'
      ? nestedPattern.percentAhead
      : typeof nestedPattern?.percentAhead === 'number'
        ? nestedPattern.percentAhead
        : typeof row.percentAhead === 'number'
          ? row.percentAhead
          : typeof row.percentAhead === 'number'
            ? row.percentAhead
            : null;
  if (row.unusualSpend !== true && nestedPattern?.unusualSpend !== true && percentAhead == null) {
    return null;
  }
  return { label: name || 'A department', percentAhead };
}

/** GET /account `unusualSpend` / `spendPattern` — vs-prior-period, not `atRisk`. */
export function unusualSpendFromEnvelope(envelope: unknown): UnusualSpendSignal[] {
  const fromList = readListFromEnvelope<Record<string, unknown>>(envelope, [
    'unusualSpend',
    'unusualSpend',
  ])
    .filter((row) => row && typeof row === 'object')
    .map(unusualSpendSignalFromRow)
    .filter((row): row is UnusualSpendSignal => Boolean(row));

  const patternObject =
    readObjectFieldFromEnvelope<Record<string, unknown>>(envelope, [
      'spendPattern',
      'spendPattern',
    ]) ?? null;
  const fromPattern =
    patternObject && !Array.isArray(patternObject)
      ? unusualSpendSignalFromRow(patternObject)
      : null;

  const merged = [...fromList];
  if (fromPattern && !merged.some((row) => row.label === fromPattern.label)) {
    merged.push(fromPattern);
  }
  return merged;
}

export async function registerCorporateAccount(body: {
  email: string;
  password: string;
  legalName: string;
  contactName: string;
  expectedVolume: string;
}) {
  const client = getBusinessClient();
  const { data, error, response } = await client.POST('/business/v1/accounts' as never, {
    body: {
      email: body.email,
      password: body.password,
      legalName: body.legalName,
      tradingName: body.legalName,
      contactName: body.contactName,
      expectedVolume: body.expectedVolume,
      billingEmail: body.email,
    } as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));

  const envelope = data as {
    data?: {
      account?: BusinessAccount;
      accessToken?: string;
      tokens?: { accessToken?: string };
    };
  };
  const token =
    envelope?.data?.accessToken ??
    envelope?.data?.tokens?.accessToken ??
    (data as { accessToken?: string })?.accessToken;
  if (token) setAccessToken(token);

  const account = accountFromEnvelope(data);
  if (account?.id) setStoredBusinessAccountId(account.id);

  return data;
}

export async function getBusinessAccount() {
  const client = getBusinessClient();
  const { data, error, response } = await client.GET('/business/v1/account');
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function updateBusinessAccount(body: Record<string, unknown>) {
  const client = getBusinessClient();
  const { data, error, response } = await client.PATCH('/business/v1/account', {
    body: body as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function getAccountVerification(): Promise<VerificationSummary> {
  const client = getBusinessClient();
  try {
    const { data, error, response } = await client.GET(
      '/business/v1/account/verification' as never,
    );
    if (response.ok && !error) {
      const account = accountFromEnvelope(data);
      const required = readListFromEnvelope<string>(data, ['requiredEvidence']);
      const items = readListFromEnvelope<VerificationEvidenceItem>(data, ['items']);
      const verificationState =
        readObjectFieldFromEnvelope<string>(data, ['verificationState']) ??
        account?.verificationState ??
        'PENDING';
      return {
        account,
        verificationState,
        requiredEvidence: required,
        items,
        source: 'verification',
      };
    }
  } catch {
    // Closest 10-06 Part 2 read: GET /account
  }

  const accountData = await getBusinessAccount();
  const account = accountFromEnvelope(accountData);
  return {
    account,
    verificationState: account?.verificationState ?? 'PENDING',
    requiredEvidence: [],
    items: [],
    source: 'account-fallback',
  };
}

export async function submitAccountVerification(body: {
  evidenceType: string;
  reference?: string;
  notes?: string;
  file?: File | null;
}) {
  const client = getBusinessClient();
  if (body.file) {
    const form = new FormData();
    form.append('evidenceType', body.evidenceType);
    if (body.reference) form.append('reference', body.reference);
    if (body.notes) form.append('notes', body.notes);
    form.append('file', body.file, body.file.name);
    const { data, error, response } = await client.POST(
      '/business/v1/account/verification/evidence' as never,
      {
        body: form as never,
        bodySerializer: ((payload: FormData) => payload) as never,
      } as never,
    );
    if (!response.ok || error) throw new Error(sdkErrorMessage(error));
    return data;
  }

  const { data, error, response } = await client.POST(
    '/business/v1/account/verification/evidence' as never,
    {
      body: {
        evidenceType: body.evidenceType,
        reference: body.reference,
        notes: body.notes,
      } as never,
    },
  );
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function goLiveBusinessAccount() {
  const client = getBusinessClient();
  const { data, error, response } = await client.POST('/business/v1/account/go-live' as never, {
    body: {} as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function loadSetupChecklist(): Promise<SetupChecklist> {
  const client = getBusinessClient();
  try {
    const { data, error, response } = await client.GET(
      '/business/v1/account/setup-checklist' as never,
    );
    if (response.ok && !error) {
      const account = accountFromEnvelope(data);
      const steps = resolveChecklistSteps(
        readListFromEnvelope<SetupChecklistStep>(data, ['steps']),
        account,
      );
      const progress =
        readObjectFieldFromEnvelope<number>(data, ['progress']) ?? account?.setupProgress ?? null;
      const goLiveReady = readObjectFieldFromEnvelope<boolean>(data, ['goLiveReady']);
      return {
        account,
        steps,
        progress: checklistProgressPercent(steps, progress),
        goLiveReady: goLiveReady ?? false,
        source: 'setup-checklist',
      };
    }
  } catch {
    // Fall through to GET /account
  }

  const accountData = await getBusinessAccount();
  const account = accountFromEnvelope(accountData);
  const steps = resolveChecklistSteps([], account);
  return {
    account,
    steps,
    progress: checklistProgressPercent(steps, account?.setupProgress),
    goLiveReady: false,
    source: 'account-fallback',
  };
}

export async function listPendingApprovals() {
  const client = getBusinessClient();
  const { data, error, response } = await client.GET('/business/v1/approvals');
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function listInvoices() {
  const client = getBusinessClient();
  const { data, error, response } = await client.GET('/business/v1/invoices');
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function listBudgets() {
  const client = getBusinessClient();
  const { data, error, response } = await client.GET('/business/v1/budgets');
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function loadAccountOverview(): Promise<AccountOverview> {
  const [accountResult, approvalsResult, invoicesResult, budgetsResult] = await Promise.allSettled([
    getBusinessAccount(),
    listPendingApprovals(),
    listInvoices(),
    listBudgets(),
  ]);

  if (accountResult.status === 'rejected') {
    throw accountResult.reason instanceof Error
      ? accountResult.reason
      : new Error(sdkErrorMessage(accountResult.reason));
  }

  const failedRegions: string[] = [];
  if (approvalsResult.status === 'rejected') failedRegions.push('pending approvals');
  if (invoicesResult.status === 'rejected') failedRegions.push('invoices');
  if (budgetsResult.status === 'rejected') failedRegions.push('budgets');

  return {
    account: accountFromEnvelope(accountResult.value),
    pendingApprovals:
      approvalsResult.status === 'fulfilled' ? itemsFromEnvelope(approvalsResult.value) : [],
    invoices: invoicesResult.status === 'fulfilled' ? itemsFromEnvelope(invoicesResult.value) : [],
    budgets: budgetsResult.status === 'fulfilled' ? itemsFromEnvelope(budgetsResult.value) : [],
    failedRegions,
  };
}
